import React, { useState, useEffect } from 'react';
import { Box, Typography, Slider, alpha, InputBase } from '@mui/material';
import { colors } from '../../../styles/theme';

export interface InvestmentSliderProps {
  /** Eigenkapitalquote in Prozent (0-100) */
  eigenkapitalquote: number;
  /** Gesamtinvestition in EUR */
  gesamtinvestition: number;
  /** Eigenkapital in EUR (berechnet) */
  eigenkapital: number;
  /** Fremdkapital in EUR (berechnet) */
  fremdkapital: number;
  /** Callback bei Änderung der Eigenkapitalquote – feuert nur bei Loslassen */
  onChange: (eigenkapitalquote: number) => void;
  /** Deaktiviert den Slider */
  disabled?: boolean;
}

/**
 * Formatiert einen Betrag in Euro mit deutschem Format
 */
const formatCurrency = (value: number): string => {
  return value.toLocaleString('de-DE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

// Farben für EK und FK
const EK_COLOR = colors.brand.teal400; // Teal - editierbar, dezenter Akzent
const FK_COLOR = colors.text.secondary; // Slate - berechnet, neutral

/**
 * 
 * Interaktiver Slider zur Anpassung der EK/FK-Aufteilung.
 * Zeigt beide Quoten und absolute Beträge gleichzeitig an.
 */
const InvestmentSlider: React.FC<InvestmentSliderProps> = ({
  eigenkapitalquote,
  gesamtinvestition,
  onChange,
  disabled = false,
}) => {
  const [localValue, setLocalValue] = useState(eigenkapitalquote);
  const [inputValue, setInputValue] = useState(eigenkapitalquote.toFixed(0));

  // Sync mit externem Wert
  useEffect(() => {
    setLocalValue(eigenkapitalquote);
    setInputValue(eigenkapitalquote.toFixed(0));
  }, [eigenkapitalquote]);

  const handleSliderChange = (_: Event, newValue: number | number[]) => {
    const val = newValue as number;
    setLocalValue(val);
    setInputValue(val.toFixed(0));
  };

  const handleSliderCommit = (_: React.SyntheticEvent | Event, newValue: number | number[]) => {
    const val = newValue as number;
    setLocalValue(val);
    setInputValue(val.toFixed(0));
    onChange(val);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleInputBlur = () => {
    const cleanValue = inputValue.replace(/[^\d]/g, '');
    const numValue = parseInt(cleanValue, 10);

    if (!isNaN(numValue)) {
      const clampedValue = Math.min(Math.max(numValue, 0), 100);
      setLocalValue(clampedValue);
      setInputValue(clampedValue.toFixed(0));
      onChange(clampedValue);
    } else {
      setInputValue(localValue.toFixed(0));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleInputBlur();
    }
  };

  // Berechnete Werte basierend auf lokalem Slider-Wert
  const localEK = (gesamtinvestition * localValue) / 100;
  const localFK = gesamtinvestition - localEK;
  const fkQuote = 100 - localValue;

  return (
    <Box
      sx={{
        position: 'relative',
        opacity: disabled ? 0.6 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
      }}
    >
      {/* EK/FK Anzeige - prominent */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        {/* Eigenkapital (links) */}
        <Box>
          <Typography
            sx={{
              color: 'text.secondary',
              fontWeight: 600,
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              mb: 1,
            }}
          >
            Eigenkapital
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
            <InputBase
              value={inputValue}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              onKeyDown={handleKeyDown}
              disabled={disabled}
              sx={{
                fontSize: '2.25rem',
                fontWeight: 700,
                fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                color: EK_COLOR,
                lineHeight: 1,
                '& input': {
                  padding: 0,
                  width: `${Math.max(inputValue.length, 2) * 0.65}em`,
                  textAlign: 'left',
                  height: '2.25rem',
                },
              }}
            />
            <Typography
              sx={{
                fontSize: '1.25rem',
                fontWeight: 600,
                color: EK_COLOR,
                lineHeight: 1,
                ml: 0.5,
              }}
            >
              %
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: '1rem',
              fontFamily: '"JetBrains Mono", "Fira Code", monospace',
              color: 'text.secondary',
              fontWeight: 500,
              mt: 0.5,
            }}
          >
            {formatCurrency(localEK)} €
          </Typography>
        </Box>

        {/* Fremdkapital (rechts) */}
        <Box sx={{ textAlign: 'right' }}>
          <Typography
            sx={{
              color: 'text.secondary',
              fontWeight: 600,
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              mb: 1,
            }}
          >
            Fremdkapital
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'flex-end' }}>
            <Typography
              sx={{
                fontSize: '2.25rem',
                fontWeight: 700,
                fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                color: FK_COLOR,
                lineHeight: 1,
              }}
            >
              {fkQuote.toFixed(0)}
            </Typography>
            <Typography
              sx={{
                fontSize: '1.25rem',
                fontWeight: 600,
                color: FK_COLOR,
                lineHeight: 1,
                ml: 0.5,
              }}
            >
              %
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: '1rem',
              fontFamily: '"JetBrains Mono", "Fira Code", monospace',
              color: 'text.secondary',
              fontWeight: 500,
              mt: 0.5,
            }}
          >
            {formatCurrency(localFK)} €
          </Typography>
        </Box>
      </Box>

      {/* Slider mit zwei Farben */}
      <Box sx={{ px: 0.5 }}>
        <Slider
          value={localValue}
          onChange={handleSliderChange}
          onChangeCommitted={handleSliderCommit}
          min={0}
          max={100}
          step={1}
          disabled={disabled}
          sx={{
            height: 12,
            '& .MuiSlider-track': {
              border: 'none',
              background: `linear-gradient(90deg, ${EK_COLOR} 0%, ${alpha(EK_COLOR, 0.8)} 100%)`,
              boxShadow: `0 2px 8px ${alpha(EK_COLOR, 0.3)}`,
            },
            '& .MuiSlider-rail': {
              background: `linear-gradient(90deg, ${alpha(FK_COLOR, 0.3)} 0%, ${FK_COLOR} 100%)`,
              opacity: 1,
            },
            '& .MuiSlider-thumb': {
              width: 24,
              height: 24,
              backgroundColor: '#fff',
              border: '3px solid',
              borderColor: localValue > 50 ? EK_COLOR : FK_COLOR,
              boxShadow: `0 2px 8px ${colors.shadow.heavy}`,
              transition: 'border-color 0.2s ease',
              '&:hover, &.Mui-focusVisible': {
                boxShadow: `0 4px 14px ${colors.shadow.heavy}`,
              },
              '&.Mui-active': {
                boxShadow: `0 4px 16px ${colors.shadow.heavy}`,
              },
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default InvestmentSlider;
